import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
  MatDialog,
} from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { ActiveSessionsService } from '../../services/active-sessions.service';
import { HttpResponse } from '@angular/common/http';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { filter, switchMap } from 'rxjs';
import { BaseModalComponent } from '@app/components/shared/base-modal/base-modal.component';
import { ActiveSession } from '@app/utils/interfaces/active-session.interface';
import { ModalControlsComponent } from '@app/components/shared/modal-controls/modal-controls.component';
import {
  CONFIRMATION_MODAL_CONFIGS,
  RESPONSE_MODAL_CONFIGS,
} from '@app/utils/constants/modal.constants';
import { ConfirmationResult } from '@app/utils/enums/confirmation-result.enum';
import { ActiveSessionsModalDetail } from '@app/utils/interfaces/active-sessions-modal-detail.interface';

@Component({
  selector: 'app-active-sessions-modal',
  standalone: true,
  imports: [MatDialogModule, TranslateModule, BaseModalComponent, ModalControlsComponent],
  templateUrl: './active-sessions-modal.component.html',
  styleUrl: './active-sessions-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveSessionsModalComponent {
  private readonly dialogRef = inject(MatDialogRef<ActiveSessionsModalComponent>);
  protected readonly session: ActiveSession = inject(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly activeSessionsService = inject(ActiveSessionsService);

  public close(): void {
    this.dialogRef.close();
  }

  public terminate(): void {
    this.close();

    const dialogRef = this.dialog.open(ConfirmationModalComponent, CONFIRMATION_MODAL_CONFIGS);

    const modalConfig = {
      data: false,
      ...RESPONSE_MODAL_CONFIGS,
    };

    dialogRef
      .afterClosed()
      .pipe(
        filter((isTerminate: ConfirmationResult) => isTerminate === ConfirmationResult.Confirm),
        switchMap(() => this.activeSessionsService.terminateSession(this.session.id)),
      )
      .subscribe({
        next: (value: HttpResponse<void>) => {
          modalConfig.data = value.ok;
          this.dialog.open(ResponseModalComponent, modalConfig);
        },
        error: () => {
          this.dialog.open(ResponseModalComponent, modalConfig);
        },
        complete: () => {
          this.close();
        },
      });
  }

  protected getDisplayedItems(): ActiveSessionsModalDetail[] {
    return [
      {
        titleKey: 'browser',
        description: this.session.browser,
      },
      {
        titleKey: 'location',
        description: this.session.location,
      },
      {
        titleKey: 'startTime',
        description: this.session.startTime,
      },
      {
        titleKey: 'lastActivityTime',
        description: this.session.lastActivityTime,
        isCurrent: this.session.isCurrent,
      },
      {
        titleKey: 'sessionId',
        description: this.session.id,
      },
      {
        titleKey: 'ipAddress',
        description: this.session.ipAddress,
      },
    ];
  }
}
